import { useMemo } from 'react';
import { StatusBadge, Button } from '../components/common';
import { calculateTotalEarnings, calculatePendingEarnings } from '../services/commissionService';

/**
 * My Earnings Page
 * For Marketers & Reps to view their commissions
 */
export function MyEarningsPage({ userCommissions, loadingCommissions }) {
  
  // Calculate totals
  const totalEarnings = useMemo(() => 
    calculateTotalEarnings(userCommissions),
    [userCommissions]
  );

  const pendingEarnings = useMemo(() => 
    calculatePendingEarnings(userCommissions),
    [userCommissions]
  );

  if (loadingCommissions) {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#94a3b8" }}>
        جاري تحميل العمولات...
      </div>
    );
  }

  return (
    <div style={{ padding: "clamp(16px, 3vw, 32px)" }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: "#f1f5f9", fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          💰 محفظتي
        </h1>
        <p style={{ color: "#94a3b8", fontSize: 14 }}>
          عرض العمولات والأرباح
        </p>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", 
        gap: 16, 
        marginBottom: 32 
      }}>
        <div style={{
          background: "linear-gradient(135deg, #052e16, #0a3d1f)",
          border: "1px solid #10b981",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#6ee7b7", fontSize: 12, marginBottom: 8 }}>إجمالي المدفوعات</p>
          <p style={{ color: "#34d399", fontSize: 32, fontWeight: 900 }}>
            {totalEarnings.toFixed(2)} AED
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #3d2000, #4d2a00)",
          border: "1px solid #f59e0b",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#fcd34d", fontSize: 12, marginBottom: 8 }}>المعلقة</p>
          <p style={{ color: "#f59e0b", fontSize: 32, fontWeight: 900 }}>
            {pendingEarnings.toFixed(2)} AED
          </p>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #162050, #1e2a6e)",
          border: "1px solid #3b82f6",
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: "#93c5fd", fontSize: 12, marginBottom: 8 }}>عدد العمولات</p>
          <p style={{ color: "#3b82f6", fontSize: 32, fontWeight: 900 }}>
            {userCommissions.length}
          </p>
        </div>
      </div>

      {/* Commissions Table */}
      <div style={{
        background: "#0c1525",
        borderRadius: 16,
        border: "1px solid #162035",
        overflow: "hidden"
      }}>
        <div style={{ padding: 20, borderBottom: "1px solid #162035" }}>
          <h2 style={{ color: "#f1f5f9", fontSize: 18, fontWeight: 700, margin: 0 }}>
            سجل العمولات
          </h2>
        </div>

        {userCommissions.length === 0 ? (
          <div style={{ padding: 48, textAlign: "center", color: "#64748b" }}>
            <p style={{ fontSize: 48, marginBottom: 16 }}>💰</p>
            <p style={{ fontSize: 16, fontWeight: 700 }}>لا توجد عمولات بعد</p>
            <p style={{ fontSize: 13, marginTop: 8 }}>
              ستظهر العمولات هنا عند إغلاق الصفقات
            </p>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#080f1e" }}>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المطعم
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    المبلغ
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    الحالة
                  </th>
                  <th style={{ padding: "12px 16px", color: "#3d5470", fontSize: 11, fontWeight: 700, textAlign: "right" }}>
                    التاريخ
                  </th>
                </tr>
              </thead>
              <tbody>
                {userCommissions.map(comm => (
                  <tr key={comm.id} style={{ borderTop: "1px solid #162035" }}>
                    <td style={{ padding: "12px 16px", color: "#f1f5f9" }}>
                      <div style={{ fontWeight: 700 }}>{comm.restaurant?.name || 'N/A'}</div>
                      <div style={{ fontSize: 11, color: "#64748b" }}>{comm.restaurant?.area || ''}</div>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#10b981", fontWeight: 700 }}>
                      {parseFloat(comm.amount).toFixed(2)} AED
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <StatusBadge 
                        status={comm.status === 'paid' ? 'deal_closed' : 'pending_closure'} 
                      />
                    </td>
                    <td style={{ padding: "12px 16px", color: "#94a3b8", fontSize: 13 }}>
                      {new Date(comm.created_at).toLocaleDateString('ar-AE')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
